import { formatNumber } from "../../../../utils/format-sub";

/**
 * §5.10's gross→bank reconciliation, one instance per currency.
 *
 * Rendered as an equation rather than a card grid: the reading order IS the
 * explanation, and a coach following "why is this smaller than that" needs
 * the lines in order with the two totals marked.
 *
 * When the window reaches back past the ledger's earliest row, or forward
 * past the last balance sync, the two balance lines are genuinely
 * underivable (§4.3) — they show a dash and a stated reason, and the final
 * line drops its "=" so it never claims to be the result of an equation
 * that could not be evaluated.
 */
const ReconciliationBlock = ({ block, t }) => {
  const money = (value) => formatNumber(value || 0, block.currency);
  // One sign style for every line (sc-46067): "− $X" / "+ $X".
  const signed = (value) =>
    value < 0 ? `− ${money(-value)}` : `+ ${money(value)}`;
  const dash = "—";

  const rows = [
    {
      key: "gross",
      label: t("income.reconcile.gross"),
      value: money(block.grossRevenue),
    },
    {
      key: "refunds",
      label: t("income.reconcile.refunds"),
      value: `− ${money(block.refunds)}`,
    },
    // The one waterfall every tab uses (sc-46058): MSB's fee and Stripe's
    // are separate lines, and net revenue is what the coach keeps.
    {
      key: "msbFee",
      label: t("income.reconcile.msbFee"),
      value: `− ${money(block.msbFee)}`,
    },
    {
      key: "stripeFees",
      label: t("income.reconcile.stripeFees"),
      value: `− ${money(block.stripeFees)}`,
    },
    {
      key: "net",
      label: t("income.reconcile.net"),
      value: money(block.netRevenue),
      total: true,
    },
  ];

  if (block.otherAdjustments) {
    rows.push({
      key: "otherAdjustments",
      label: t("income.reconcile.otherAdjustments"),
      value: signed(block.otherAdjustments),
    });
    // What it's made of, by Stripe reporting category (sc-46067).
    (block.adjustments || []).forEach((adjustment) => {
      rows.push({
        key: `adjustment-${adjustment.category}`,
        label: t(`income.reconcile.categories.${adjustment.category}`, {
          defaultValue: adjustment.category.replace(/_/g, " "),
        }),
        value: signed(adjustment.amount),
        sub: true,
      });
    });
  }

  rows.push(
    {
      key: "balanceIn",
      label: t("income.reconcile.balanceIn"),
      value: block.balanceIn == null ? dash : `+ ${money(block.balanceIn)}`,
    },
    {
      key: "balanceOut",
      label: t("income.reconcile.balanceOut"),
      value: block.balanceOut == null ? dash : `− ${money(block.balanceOut)}`,
    },
    {
      key: "paidToBank",
      label: block.closes
        ? t("income.reconcile.paidToBank")
        : t("income.reconcile.paidToBankNoEquation"),
      value: money(block.paidToBank),
      total: true,
    }
  );

  return (
    <div className="reconciliation-block">
      <header className="reconciliation-block__header">
        <h4>
          {t("income.reconcile.heading", {
            currency: block.currency.toUpperCase(),
          })}
        </h4>
      </header>
      <table className="reconciliation-table">
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              data-total={row.total ? "true" : undefined}
              data-sub={row.sub ? "true" : undefined}
            >
              <td>{row.label}</td>
              <td className="amount">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {block.balanceUnavailableReason && (
        <p className="reconciliation-note">
          {t(`income.reconcile.unavailable.${block.balanceUnavailableReason}`)}
        </p>
      )}
      <p className="reconciliation-note">
        {t("income.reconcile.refundTimingNote")}
      </p>
    </div>
  );
};

export default ReconciliationBlock;
